"use client";

import { Link } from "@/i18n/navigation";
import Image from "next/image";

export interface BlogPost {
  id: string;
  title_en: string;
  title_ar: string | null;
  slug: string;
  excerpt_en: string | null;
  excerpt_ar: string | null;
  content_md_en: string | null;
  content_md_ar: string | null;
  cover_image_url: string | null;
  category: string | null;
  tags: string[];
  published: boolean;
  published_at: string | null;
  author_id: string | null;
  created_at: string;
}

export function BlogCard({
  post,
  locale,
}: {
  post: BlogPost;
  locale: string;
}) {
  const title =
    locale === "ar" ? post.title_ar || post.title_en : post.title_en;
  const excerpt =
    locale === "ar" ? post.excerpt_ar || post.excerpt_en : post.excerpt_en;

  const formattedDate = post.published_at
    ? new Date(post.published_at).toLocaleDateString(
        locale === "ar" ? "ar-SA" : "en-US",
        { year: "numeric", month: "long", day: "numeric" }
      )
    : null;

  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group block rounded-lg border border-(--fm-border) bg-(--fm-surface) overflow-hidden hover:border-(--fm-accent)/40 transition-colors"
    >
      {/* Cover image or placeholder gradient */}
      <div className="relative aspect-[16/10] overflow-hidden">
        {post.cover_image_url ? (
          <Image
            src={post.cover_image_url}
            alt={title}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-(--fm-accent)/20 to-(--fm-accent)/5" />
        )}
        {/* Category badge */}
        {post.category && (
          <span className="absolute top-3 left-3 rtl:left-auto rtl:right-3 px-2.5 py-1 text-xs font-medium rounded-full bg-(--fm-background)/80 text-(--fm-accent) backdrop-blur-sm">
            {post.category}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading text-lg font-bold text-(--fm-text-primary) mb-1 group-hover:text-(--fm-accent) transition-colors">
          {title}
        </h3>
        {formattedDate && (
          <p className="text-xs text-(--fm-text-secondary) mb-2">
            {formattedDate}
          </p>
        )}
        {excerpt && (
          <p className="text-sm text-(--fm-text-secondary) line-clamp-2 leading-relaxed mb-3">
            {excerpt}
          </p>
        )}
        <span className="text-sm text-(--fm-accent) font-medium group-hover:underline">
          {locale === "ar" ? "اقرأ المزيد ←" : "Read more →"}
        </span>
      </div>
    </Link>
  );
}
